import {
  getCheggAuthRedirectUrl,
  getCheggAuthVersion,
  getSAProfile,
} from '@src/state/site/selectors'
import site from '@src/state/site/site'
import * as qs from 'qs'
import { LOCAL_OR_PLAYGROUND } from './localOrPlayground'
import { AuthActionType } from '@chegg/one-auth-sdk'
type SchoolType = import('@src/state/analytics/rsw/eventSchemas').SchoolType

export type AuthService = 'apple' | 'google' | 'chegg' | 'facebook' | 'internships'
export interface AuthSuccessGrant {
  access_token: string | null
  auth_flow: 'code'
  auth_method: AuthService | 'Chegg' | 'Facebook' | 'Google'
  auth_provider_token?: string | null
  auth_type: AuthActionType
  code?: string
  id_token: string | null
  redirect_uri?: string
}
export interface AuthEventData {
  eventInfo: AuthEventInfo
  eventType: string
}

export type SignupUserType =
  | 'student'
  | 'parent'
  | 'teacher'
  | 'tutor'
  | 'chegg_internal'
  | 'educator'
  | undefined

export interface AuthEventInfo {
  clientId: string
  destination_url: string
  profileId: string
  signin_viewed?: string
  signup_viewed?: string
  signup_user_type_selected?: SignupUserType
  signup_teacher_teaching_in_selected?: SchoolType
  signup_student_type_selected?: SchoolType
  type?: string
  version: string
}

const appendElementToBody = (elementName: string, attributes: any) => {
  const element = document.createElement(elementName)
  attributes.forEach((attribute: any) => {
    element.setAttribute(attribute.name, attribute.value)
  })
  document.body.appendChild(element)
}

// Chegg Auth Component Documentation:
// https://confluence.cheggnet.com/pages/viewpage.action?spaceKey=GE&title=K+-+Auth+open+component

// Chegg Auth Usage:
// https://docs.google.com/document/d/1xD0RFOjbVdDddtgHIjacAewAIt4k7cjBHcSKDtesuqE

const baseUrlDomain = () =>
  LOCAL_OR_PLAYGROUND ? 'chegg-registry.test.cheggnet' : 'registry.chegg'

const baseUrl = () =>
  `https://${baseUrlDomain()}.com/chegg-auth/${getCheggAuthVersion(site)}`

export const getCheggAuthUrl = (customAuthOptions?: any) => {
  const authOptions = {
    profile: getSAProfile(site), // 'EB',
    display: 'dialog',
    defaultflow: 'signup',
    show: 0,
    redirect_uri: getCheggAuthRedirectUrl(site),
    fullpkg: 1,
    ...customAuthOptions,
  }

  const customParams = {
    signin: { subtitle: 'to enable this feature' },
    signup: { subtitle: 'to enable this feature' },
  }

  // the data itself needs to be base64 encoded, which is what .btoa() does.
  // To be clear, btoa is a native browser javascript method available on window
  authOptions.data = window.btoa(JSON.stringify({ customParams }))

  if (LOCAL_OR_PLAYGROUND) {
    authOptions.providerurl = 'https://rc.live.auth.test.cheggnet.com/'
  }

  const parameters = qs.stringify(authOptions, { addQueryPrefix: true })

  return `${baseUrl()}${parameters}`
}

const cheggAuthReady = (
  e: Event | null,
  auth: any,
  successCallback: (saOptions: AuthSuccessGrant) => void,
  hideCallback: () => void,
  authEventCallback: (event: Event, data: AuthEventData) => void
) => {
  auth.trigger('disable redirect', true)
  auth.on('auth event', authEventCallback)
  auth.on('auth successful', (event: Event, saOptions: AuthSuccessGrant) => {
    successCallback(saOptions)
  })
  auth.on('hidden', hideCallback)
}

export const loadCheggAuthComponent = (
  onReadyCb: () => void,
  successCallback: (saOptions: AuthSuccessGrant) => void,
  hideCallback: () => void,
  authEventCallback: (event: Event, data: AuthEventData) => void
) => {
  appendElementToBody('oc-component', [{ name: 'href', value: getCheggAuthUrl() }])

  window.oc = window.oc || {}
  window.oc.cmd = window.oc.cmd || []
  window.oc.cmd.push((oc: any) => {
    if (window.globalCheggAuth) {
      onReadyCb()
      cheggAuthReady(
        null,
        window.globalCheggAuth,
        successCallback,
        hideCallback,
        authEventCallback
      )
    } else {
      oc.events.on('chegg-auth-ready', (e: any, auth: any) => {
        onReadyCb()
        cheggAuthReady(e, auth, successCallback, hideCallback, authEventCallback)
      })
    }
  })
}

export const showCheggSuperAuth = (experience: string = 'login') => {
  window.oc = window.oc || {}
  window.oc.cmd = window.oc.cmd || []
  window.oc.cmd.push((oc: any) => {
    if (window.globalCheggAuth) {
      window.globalCheggAuth.trigger('set default experience', experience)
      window.globalCheggAuth.trigger('show')
    } else {
      oc.events.on('chegg-auth-ready', (e: any, auth: any) => {
        auth.trigger('set default experience', experience)
        auth.trigger('show')
      })
    }
  })
}



// WEBPACK FOOTER //
// ./src/utils/cheggAuth.ts